import {
  CacheKeys,
  ErrorBoundary,
  useBlockPermission,
  useModulePermission,
  useSessionStorage,
} from '@bechtel/client-common';
import { Alert, Box, Heading, Spinner, SpinnerSize, Text, useModalState } from '@bechtel/gds';
import { BlockType, CommodityType, MilestoneType } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { map } from 'lodash';
import { Suspense, useCallback, useRef, useState } from 'react';
import { BlockTypeMessages } from '~/components';
import { BlockStatusProps } from './drawer/block-status';
import BlockSearchDrawer from './drawer/internals';
import { BlockDrawerValuesProps, defaultValues } from './form-data';
import BlockList from './list/internals';
import { BlockCardSearch } from './search/internals';
import { BlockCardHeaderWrapperStyled, BlockDetailContainer, TextStyled } from './styled';

const Blocks = () => {
  const { i18n } = useLingui();
  const { canViewBlockModule } = useModulePermission();
  const { canViewBlocks } = useBlockPermission();
  const [searchQuery, setSearchQuery] = useState('');
  const persistedDrawerValues = useRef(defaultValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<BlockDrawerValuesProps>(
    CacheKeys.BlockDrawerValues,
    persistedDrawerValues.current || defaultValues
  );
  // NOTE: used for toggling advanced search drawer
  const { close, toggle, visible } = useModalState(false);
  const onDrawerSubmit = useCallback(
    (values: BlockDrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  if (!canViewBlockModule) return <Alert>Sorry, Block module is not activated for this project.</Alert>;
  if (!canViewBlocks) {
    return (
      <Alert>
        <Trans>Sorry, you do not have rights to view blocks</Trans>
      </Alert>
    );
  }
  const commodityTypes = map(drawerValues.commodityTypes, (item: CommodityType) => item.name);
  const milestoneNameList = map(drawerValues.milestoneList, (item: MilestoneType) => item.name);
  const blockTypes = map(drawerValues.blockTypes, (item: BlockType) => i18n._(BlockTypeMessages[item.title].id));
  const blockStatus = map(drawerValues.blockStatus, (item: BlockStatusProps) => item.label);
  const filterByLabel = [...commodityTypes, ...milestoneNameList, ...blockTypes, ...blockStatus].join(', ');
  const scopeByLabel = drawerValues.searchScope.name;
  const searchByLabel = drawerValues.searchBy.label;

  return (
    <ErrorBoundary>
      <BlockDetailContainer>
        <BlockCardHeaderWrapperStyled>
          <Box pb={{ sm: '500' }} mb={{ sm: '200' }}>
            <Heading size="600" color="grey.1000">
              <Trans>Blocks</Trans>
            </Heading>
          </Box>
          <BlockCardSearch
            toggle={toggle}
            defaultValues={defaultValues}
            drawerValues={drawerValues}
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
          />
          <Box flexDirection={{ sm: 'column' }} pb={{ sm: '500' }} style={{ maxWidth: '292px' }}>
            {!(scopeByLabel.length > 0) && !(searchByLabel.length > 0) && !(filterByLabel.length > 0) && (
              <Box mb={{ sm: '100' }} pl={{ sm: '100' }}>
                <Text size="250">Searching All</Text>
              </Box>
            )}
            {scopeByLabel.length > 0 && (
              <Box mb={{ sm: '100' }}>
                <TextStyled size="250" weight="600">
                  <Trans>Showing:</Trans> &nbsp;
                  <Text weight="500">{i18n._(scopeByLabel)}</Text>
                </TextStyled>
              </Box>
            )}
            {searchByLabel.length > 0 && (
              <Box mb={{ sm: '100' }}>
                <TextStyled size="250" weight="600">
                  <Trans>Searching By:</Trans>&nbsp;
                  <Text weight="500">{i18n._(searchByLabel)}</Text>
                </TextStyled>
              </Box>
            )}
            {filterByLabel.length > 0 && (
              <Box>
                <TextStyled size="250" weight="600">
                  <Trans>Filtering By:</Trans>&nbsp;
                  <Text weight="500">{i18n._(filterByLabel)}</Text>
                </TextStyled>
              </Box>
            )}
          </Box>
        </BlockCardHeaderWrapperStyled>
        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
          <BlockList searchQuery={searchQuery} drawerValues={drawerValues} />
        </Suspense>
      </BlockDetailContainer>
      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
        {visible && <BlockSearchDrawer close={close} initialValues={drawerValues} onSubmit={onDrawerSubmit} />}
      </Suspense>
    </ErrorBoundary>
  );
};

export default Blocks;
